import dayjs from 'dayjs';
import { handleActions } from 'redux-actions';
import { baseUrl, MEASURE } from '../constants';
import { getSession, setSession } from './session';
import { request } from './request';

const getNewPrice = (price, condition, value, persent) => {
  if (!price) return 0;
  if (condition === 'equal') return value;
  if (persent) {
    const persentPrice = Math.round(price * (value / 100));
    if (condition === 'plus') {
      return price + persentPrice;
    }
    return price - persentPrice;
  }
  if (condition === 'plus') {
    return price + value;
  }
  if (condition === 'multiply') {
    return price * value;
  }
  return price - value;
};

export const identityPayload = (state, action) => {
  if (action.payload !== undefined) return action.payload;
  return state;
};

export const identityClean = (initial) => () => initial;

export const forAction = (action, handler) => [action, handler];
export const forCleanAction = (action, handler) => [action, handler];

export const createCleanActions = (actions, initial) => actions.map(
  (action) => forCleanAction(action, identityClean(initial)),
);

export const createReducer = (actions, initial, cleanActions = []) => {
  const cleanActionsHandlers = createCleanActions(cleanActions, initial);

  return handleActions(new Map([...actions, ...cleanActionsHandlers]), initial);
};

export const propNameSelector = (selector, field) => (state) => selector(state)[field];

export const regexpPhone = (phone) => {
  const tPhone = phone
    .replace(/\s/g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .replace(/-/g, '')
    .replace(/\+/g, '');
  return tPhone;
};

export const makeActionType = (namespace, items) => `SB/${namespace}/${items.join('/')}`;

export const numberDevider = (x) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

export const declOfNum = (number, titles, onlyTitle) => {
  const cases = [2, 0, 1, 1, 1, 2];
  const result = (number % 100 > 4 && number % 100 < 20)
    ? 2
    : cases[(number % 10 < 5)
      ? number % 10
      : 5];

  if (onlyTitle) return titles[result];
  return `${numberDevider(number)} ${titles[result]}`;
};

export const formatPhone = (value) => {
  const strValue = `${value}`;
  return `+7 (${strValue.slice(1, 4)}) ${strValue.slice(4, 7)}-${strValue.slice(7, 9)}-${strValue.slice(9, 11)}`;
};

export const sortOrderByDate = (a, b) => new Date(b.created_at) - new Date(a.created_at);

const getSessionTime = () => {
  const time = getSession('time');
  if (!time || !time.server || !time.client) return null;
  return time;
};

const parseCustomDate = (dateStr) => {
  const [date, times] = dateStr.split(' ');
  const [day, month, year] = date.split('.');
  return dayjs(`${year}-${month}-${day} ${times}`);
};

const calculateTime = (time) => {
  const { server, client } = time;
  const currentTime = dayjs();
  const serverTime = dayjs(parseCustomDate(server));
  const clientTime = dayjs(parseCustomDate(client));

  const diffMin = currentTime.diff(clientTime, 'minute');
  const result = serverTime.add(diffMin, 'minute');

  return result.format('HH:mm');
};

const getServerTime = async (timeZone) => {
  try {
    const server = await request({ method: 'get', url: '/cities/getTime', params: { timeZone } });
    if (server.error) return dayjs().format('HH:mm');
    const client = dayjs().format('DD.MM.YYYY HH:mm');
    setSession('time', { server, client });
    const result = parseCustomDate(server);
    return result.format('HH:mm');
  } catch (error) {
    return dayjs().format('HH:mm');
  }
};

export const getCurrentTime = async (timeZone = '+05') => {
  const time = getSessionTime();
  if (time && time.server && parseCustomDate(time.server).format('DD.MM.YYYY') === dayjs().format('DD.MM.YYYY')) return calculateTime(time);
  const result = await getServerTime(timeZone);
  return result;
};

export const checkTimeWorkZone = async ({
  city,
  zoneData = null,
  zonesData = null,
  timeMethod = 'fast',
  selectedTime = new Date(),
}) => {
  const getZoneStartDelivery = (zone) => (zone && zone.timetableWork && zone.timetableWork.startDelivery) || '10:00';
  const getZoneEndDelivery = (zone) => (zone && zone.timetableWork && zone.timetableWork.endDelivery) || '22:45';

  let workedZone = zoneData && zoneData.id ? zoneData : null;
  const startDeliveryWorkedZone = workedZone ? getZoneStartDelivery(workedZone) : null;
  const endDeliveryWorkedZone = workedZone ? getZoneEndDelivery(workedZone) : null;
  const currentTime = await getCurrentTime(city.timeZone || '+05');

  const getTimeWorkZonesDelivery = (type, zones) => {
    const timeStart = '09:00';
    if (type === 'start') {
      const minTimeZone = zones.filter((zone) => {
        const zoneStartDelivery = getZoneStartDelivery(zone);
        return zoneStartDelivery > timeStart;
      })
        .reduce((minTime, zone) => {
          const zoneStartDelivery = getZoneStartDelivery(zone);
          if (!minTime || zoneStartDelivery < getZoneStartDelivery(minTime)) return zone;
          return minTime;
        }, null);
      return getZoneStartDelivery(minTimeZone);
    }

    const maxTimeZone = zones.reduce((maxTime, zone) => {
      const zoneEndDelivery = getZoneEndDelivery(zone);
      const isNight = zoneEndDelivery < timeStart && zoneEndDelivery >= '00:00';
      const isNightMaxTime = getZoneEndDelivery(maxTime) < timeStart && getZoneEndDelivery(maxTime) >= '00:00';
      if (!maxTime) return zone;
      if (((!isNight && !isNightMaxTime) || (isNight && isNightMaxTime))) {
        if (zoneEndDelivery > getZoneEndDelivery(maxTime)) return zone;
        return maxTime;
      }
      if (isNight && !isNightMaxTime) return zone;
      return maxTime;
    }, null);
    return getZoneEndDelivery(maxTimeZone);
  };

  const checkZone = (time, start, end) => {
    if (end < '09:00') return time >= start || time <= end;
    return time >= start && time <= end;
  };

  if (timeMethod === 'fast') {
    if (!checkZone(currentTime, startDeliveryWorkedZone, endDeliveryWorkedZone)) {
      if (zonesData) {
        workedZone = zonesData.find((item) => {
          const start = getZoneStartDelivery(item);
          const end = getZoneEndDelivery(item);
          return checkZone(currentTime, start, end);
        });
      } else {
        return { ...workedZone, notWork: true };
      }
    }
  } else {
    const selectedTimeDate = `${String(dayjs(selectedTime).hour()).padStart(2, '0')}:${String(dayjs(selectedTime).minute()).padStart(2, '0')}`;
    if (!checkZone(selectedTimeDate, startDeliveryWorkedZone, endDeliveryWorkedZone)) {
      if (zonesData) {
        workedZone = zonesData.find((item) => {
          const start = getZoneStartDelivery(item);
          const end = getZoneEndDelivery(item);
          return checkZone(selectedTimeDate, start, end);
        });
      } else {
        return { ...workedZone, notWork: true };
      }
    }
  }

  if (!workedZone && zonesData) {
    workedZone = {
      ...zonesData[0],
      notWork: true,
      timetableWork: {
        startDelivery: getTimeWorkZonesDelivery('start', zonesData),
        endDelivery: getTimeWorkZonesDelivery('end', zonesData),
      },
    };
  }

  return workedZone;
};

export const getTimeWork = async ({
  city,
  delivery,
  timeMethod = 'fast',
  selectedTime = new Date(),
  point = null,
  zoneData = null,
}) => {
  if (!delivery) return { isWork: true, message: '' };
  const getStartDelivery = (item) => (item && item.timetableWork && item.timetableWork.startDelivery) || '10:00';
  const getEndDelivery = (item) => (item && item.timetableWork && item.timetableWork.endDelivery) || '22:45';
  const getStartOwn = (item) => (item && item.timetableWork && item.timetableWork.startOwn) || '10:00';
  const getEndOwn = (item) => (item && item.timetableWork && item.timetableWork.endOwn) || '22:55';

  const getTimeTableData = () => {
    if (delivery === 'delivery' && zoneData && zoneData.point && zoneData.point.id) return zoneData.point;
    if (delivery === 'own' && point && point.id) return point;
    return city;
  };

  const timeTableData = await getTimeTableData();
  const startDelivery = await getStartDelivery(timeTableData);
  const endDelivery = await getEndDelivery(timeTableData);
  const startOwn = await getStartOwn(timeTableData);
  const endOwn = await getEndOwn(timeTableData);
  const zoneStartDelivery = await getStartDelivery(zoneData);
  const zoneEndDelivery = await getEndDelivery(zoneData);
  const currentTime = await getCurrentTime(city.timeZone || '+05');

  const getTime = (type, deliveryTime, ownTime) => {
    const timeStart = '09:00';
    const isNightDelivery = deliveryTime < timeStart && deliveryTime >= '00:00';
    const isNightOwn = ownTime < timeStart && ownTime >= '00:00';

    if (type === 'start') {
      if ((!isNightDelivery && !isNightOwn) || (isNightDelivery && isNightOwn)) {
        if (deliveryTime < ownTime) return deliveryTime;
        return ownTime;
      }

      if (isNightDelivery && !isNightOwn) return ownTime;
      return deliveryTime;
    }

    if ((!isNightDelivery && !isNightOwn) || (isNightDelivery && isNightOwn)) {
      if (deliveryTime > ownTime) return deliveryTime;
      return ownTime;
    }

    if (isNightDelivery && !isNightOwn) return deliveryTime;
    return ownTime;
  };

  const isTimeInRange = (time, start, end) => {
    if (end < '09:00') return time >= start || time <= end;
    return time >= start && time <= end;
  };

  if (!isTimeInRange(currentTime, getTime('start', startDelivery, startOwn), getTime('end', endDelivery, endOwn))) {
    return { isWork: false, message: `Время работы: c ${getTime('start', startDelivery, startOwn)} по ${getTime('end', endDelivery, endOwn)}` };
  }

  if (timeMethod === 'fast') {
    if (delivery === 'delivery') {
      if (!isTimeInRange(currentTime, startDelivery, endDelivery)) {
        return { isWork: false, message: `Время работы доставки: c ${startDelivery} по ${endDelivery}` };
      }
      if (zoneData) {
        if (zoneData.notWork || !isTimeInRange(currentTime, zoneStartDelivery, zoneEndDelivery)) return { isWork: false, message: `Время работы доставки по вашему адресу: c ${zoneStartDelivery} по ${zoneEndDelivery}` };
      }
    } else if (!isTimeInRange(currentTime, startOwn, endOwn)) {
      return { isWork: false, message: `Время работы на вынос: c ${startOwn} по ${endOwn}` };
    }
  } else {
    const selectedTimeDate = `${String(dayjs(selectedTime).hour()).padStart(2, '0')}:${String(dayjs(selectedTime).minute()).padStart(2, '0')}`;

    if (delivery === 'delivery') {
      if (!isTimeInRange(selectedTimeDate, startDelivery, endDelivery)) {
        return { isWork: false, message: `Время работы доставки: c ${startDelivery} по ${endDelivery}` };
      }
      if (zoneData && zoneData.notWork) {
        return { isWork: false, message: `Время работы доставки по вашему адресу: c ${zoneStartDelivery} по ${zoneEndDelivery}` };
      }
    } else if (!isTimeInRange(selectedTimeDate, startOwn, endOwn)) {
      return { isWork: false, message: `Время работы на вынос: c ${startOwn} по ${endOwn}` };
    }
  }

  return { isWork: true, message: '' };
};

export const getImageUrl = (image, host) => `${host || ''}${baseUrl}${image?.url}`;
export const getGoodUrl = (category, host) => `${host}/menu/${category}`;

export const getModifiedGood = (good, idMod) => {
  const mod = good.modifications
    && good.modifications.find((item) => String(item.id) === String(idMod));
  if (!mod) return good;

  const modified = { ...good };
  if (mod.price) {
    modified.price = getNewPrice(modified.price, mod.condition, mod.price, false);
  }
  if (mod.oldPrice) {
    modified.oldPrice = getNewPrice(modified.oldPrice, mod.condition, mod.oldPrice, false);
  }
  if (mod.weight) {
    modified.weight = mod.weight;
  }

  modified.isDefault = mod.default;
  modified.activeMod = mod.id;
  modified.activeModName = mod.title;

  return modified;
};

export const getWeightAndMeasure = (weight, measure) => {
  const result = {};
  result.weight = weight ? numberDevider(weight) : null;
  if (measure) {
    result.measure = MEASURE[measure];
  } else {
    result.measure = {
      title: 'Вес',
      value: 'гр.',
    };
  }
  return result;
};
